<script lang='ts'>

    /* IMPORTS --------------------------------------------------------------------------------- */

    // STYLING
    import 'tailwindcss/tailwind.css';

    // CASSIOPEIA
    import {
        AuctionDownload,
        Badge,
        Button,
        Card,
        currency,
        Definition,
        Heading,
        Icon,
        integer,
        Link,
        number,
        OutputAuthenticityCode,
        OutputGradeCode,
        OutputLocations,
        OutputShipping,
        percentage,
        Small,
    } from '@smatch/cassiopeia-web-components';

    import { BadgeNew, BadgeReserved, BadgePrivate } from 'src/app/routes/auctions/components/index.js';

    /* TYPES ----------------------------------------------------------------------------------- */

    import type { Auction } from 'src/app/types';

    /* EXPORTS --------------------------------------------------------------------------------- */

    export let auction: Auction;

    /* INTERNALS -------------------------------------------------------------------------------- */

    const BRAND_COUNT = 5;
    const BRANDS = auction.brands ? auction.brands.filter(brand => brand.logo !== undefined) : [];

    const IS_AUCTION_OPEN = auction._.state === 'open';

    $: url = `/auctions/${ auction._.key }`;
    $: ({ currencyCode } = auction.locale);
    $: hasPricePerKilo
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        = auction.types?.includes('off_price')
        && auction.offer.average.perKilogram !== undefined
        && auction.offer.average.perKilogram > 0;
</script>

<Card css = { `!relative ${ IS_AUCTION_OPEN ? '' : 's8-not-open' } s8-z-index` } >

    <div class = 's8-wrap'>

        <div class = 's8-wrap-top'>

            <div class = 's8-wrap-heading'>

                <Heading size = 'md'>
                    { auction.name }
                    {#if auction.duration?.start !== undefined }
                        <BadgeNew date = { auction.duration.start } />
                    {/if}
                    {#if auction.isReserved }
                        <BadgeReserved/>
                    {/if}
                    {#if auction.accessCode === 'private'}
                        <BadgePrivate />
                    {/if}
                </Heading>

                <div class = 's8-subheading'>

                    {#if Array.isArray(auction.categories) }
                        <ul class = 's8-categories'>
                            {#each auction.categories as category}
                                <li>{ category.name }</li>
                            {/each}
                        </ul>
                    {/if}

                    {#if Array.isArray(auction.brands) && auction.brands.length > 1 }
                        <ul class = 's8-brands'>
                            {#each BRANDS as brand, index }
                                {#if index < BRAND_COUNT && brand.logo !== undefined }
                                    <li>
                                        <img src = { brand.logo.url } alt = { brand.name }/>
                                    </li>
                                {/if}
                            {/each}
                            {#if auction.brands.length > BRAND_COUNT }
                                <li class = 's8-counter'>
                                    <Badge color = 'dark' style = 'strong'>
                                        +{ auction.brands.length - BRAND_COUNT }
                                    </Badge>
                                </li>
                            {/if}
                        </ul>
                    {/if}

                </div>

            </div>

            {#if auction.brands?.length === 1 && auction.brands[0].logo !== undefined}
                <div class = 's8-brand'>
                    <img src = { auction.brands[0].logo.url } alt = { auction.brands[0].name }/>
                </div>
            {/if}

        </div>

        {#if auction.bid?.buyNow !== undefined }
            <div class = 's8-discount'>
                <Definition>
                    <svelte:fragment slot = 'content'>
                        {#if hasPricePerKilo}
                            <span class = 's8-discount-inner'>
                                { currency(
                                    auction.offer.average.perKilogram ?? 0,
                                    currencyCode,
                                    false,
                                ) }/Kg
                            </span>
                        {:else if auction.discount > 0}
                            <span class = 's8-discount-inner'>
                                { percentage(auction.discount) } off RRP
                            </span>
                        {/if}
                    </svelte:fragment>
                    <svelte:fragment slot = 'tooltip'>
                        Buy Now for { currency(auction.bid.buyNow, currencyCode, false) }<br />
                        {#if hasPricePerKilo}
                            at { currency(
                                auction.offer.average.perKilogram ?? 0,
                                currencyCode,
                                false,
                            ) } per Kilo
                        {:else if auction.discount > 0}
                            and save { percentage(auction.discount) } on the <br />
                            Recommended Retail Price
                        {/if}
                    </svelte:fragment>
                </Definition>
            </div>
        {/if}

        {#if auction.images }
            <Link { url }>
                <ul class = 's8-images'>
                    <!-- eslint-disable-next-line no-unused-vars -->
                    {#each Array(6) as _, index}
                        <li class = 's8-image'>
                            <!-- Sometimes the BE delivers weird shit.  -->
                            <!-- eslint-disable-next-line @typescript-eslint/no-unnecessary-condition  -->
                            {#if auction.images[index]?.url}
                                <img
                                    src = { auction.images[index].url }
                                    alt = { auction.name }
                                />
                            {/if}
                        </li>
                    {/each}
                </ul>
            </Link>
        {/if}

        <div class = 's8-wrap-bottom'>

            <ul class = 's8-info'>
                <li>
                    <Icon key = 'tag' />
                    <strong>Offer:</strong>
                    {#if hasPricePerKilo}
                        { currency(
                            auction.offer.average.perKilogram ?? 0,
                            currencyCode,
                            false,
                        ) } per Kilo<br />
                    {:else}
                        { currency(
                            auction.offer.average.perItem,
                            currencyCode,
                            false,
                        ) } per item<br />
                    {/if}
                    <Small color = 'dark'>
                        Avg. Retail Price:
                        { currency(auction.average.price.retail.recommended, currencyCode, false) } per item
                    </Small>
                </li>
                {#if auction.bid?.minimum?.value !== undefined}
                    <li>
                        <Icon key = 'basket' />
                        <strong>Min. Order:</strong>
                        {#if auction.allowsPartialBids}
                            {#if auction.bid.minimum.items !== undefined}
                                { number(auction.bid.minimum.items, 0, false) }
                            {/if}
                        {:else}
                            { number(auction.total.quantity.items, 0, false) }
                        {/if}
                        Items <br />
                        <Small color = 'dark'>
                            Available: { integer(auction.total.quantity.items, false) }
                            items over { integer(auction.total.quantity.products, false) } styles
                        </Small>
                    </li>
                {/if}
                {#if auction.grade !== undefined}
                    <li>
                        <Icon key = 'badgeCheck' />
                        <strong>Quality:</strong> <OutputGradeCode code = { auction.grade.code } />
                        {#if auction.grade.description}, { auction.grade.description }{/if}<br />
                        <Small color = 'dark'>
                            Authenticity: <OutputAuthenticityCode code = { auction.authenticityCode } />
                        </Small>
                    </li>
                {/if}
                <li class = 's8-addresses'>
                    {#if auction.addressKeys !== undefined && auction.addressKeys.length > 0}
                        <Icon key = 'location' />
                        <strong>Locations:</strong>
                        <OutputLocations addressKeys = { auction.addressKeys } separator = 'comma' />
                    {/if}

                    {#if auction.shipping?.options !== undefined}
                        <Small color = 'dark'>
                            Shipping:
                            {#each auction.shipping.options as option}
                                <OutputShipping { option } />
                            {/each}
                        </Small>
                    {/if}
                </li>
            </ul>

            <div class = 's8-buttons'>
                <ul>
                    <li>
                        <Button { url } size = 'sm' label = 'View Deal' icon = 'arrowRight' />
                    </li>

                    <li>
                        <AuctionDownload auctionKey = { auction._.key } let:data>

                            <!-- TODO: Fix slot types -->
                            <!-- eslint-disable @typescript-eslint/no-unsafe-member-access -->

                            {#if Array.isArray(data) && data.length > 0}
                                <ul class = 's8-download'>
                                    {#each data as item}
                                        <!-- Keep this check to ensure if more formats are added it will not break -->
                                        <!-- eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -->
                                        {#if item.type === 'xlsx'}
                                            <Button
                                                icon = 'download'
                                                style = 'solid'
                                                label = 'Download Excel'
                                                url = { item.url }
                                                size = 'sm'
                                                color = 'dark'
                                            />
                                        {/if}
                                    {/each}
                                </ul>
                            {/if}

                            <!-- eslint-enable -->

                        </AuctionDownload>
                    </li>
                </ul>
            </div>

        </div>

    </div>

</Card>

<!-- eslint-disable max-lines -->


<style lang="postcss">

    :global(.s8-not-open) {
        background-image: repeating-linear-gradient(135deg, #ddd, #ddd 10px, #eee 10px, #eee 20px);
    }

    :global(.s8-z-index) {
        z-index: 0;
    }

    div.s8-wrap {
        container-type: inline-size;
        container-name: wrap;

        div.s8-wrap-top {
            display: flex;
            justify-content: space-between;
            align-items: top;

            div.s8-wrap-heading {
                flex: 1 1 auto;

                :global(h3) {
                    margin-bottom: 0px;
                }

                div.s8-subheading {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    color: rgba(39,39,17,0.32);
                    margin-bottom: 10px;

                    ul {
                        display: flex;
                        column-gap: 1vw;
                        align-items: center;

                        &.s8-brands {
                            img {
                                max-height: 15px;
                                max-width: 60px;
                                width: 100%;
                            }
                        }
                    }
                }
            }

            div.s8-brand img {
                max-height: 40px;
                max-width: 100px;
                width: 100%;
            }
        }

        ul.s8-images {
            display: flex;
            border-radius: 10px;
            overflow: hidden;
            align-items: center;
            justify-content: space-evenly;
            margin-bottom: 20px;

            li {
                min-width: 16.6%;

                img {
                    max-height: 210px;
                    width: 100%;
                }
            }
        }

        div.s8-discount {
            position: absolute;
            right: 0px;
            z-index: 10;
            margin-top: 20px;

            span.s8-discount-inner {
                background-color: #EF233B;
                color: #ffffff;
                padding: 4px 12px;

                --mask: conic-gradient(from 45deg at left,#0000,#000 1deg 89deg,#0000 90deg) 50%/100% 6.00px;
                -webkit-mask: var(--mask);
                mask: var(--mask);

            }
        }

        div.s8-wrap-bottom {
            display: flex;
            column-gap: 20px;

            ul.s8-info {
                display: flex;
                flex-wrap: wrap;
                flex: 1 1 auto;
                gap: 10px;
                container-type: inline-size;
                container-name: info;
                li {
                    width: calc(33.3% - 20px/3);

                    strong {
                        font-weight: 600;
                    }

                    :global(svg) {
                        display: inline-block;
                    }
                }
                li.s8-addresses {
                    flex: 1 1 100%;

                }
            }

            div.s8-buttons {
                container-type: inline-size;
                container-name: buttons;
                min-width: 150px;

                ul {
                    display: flex;
                    flex-direction: row;
                    column-gap: 10px;

                    li {
                        width: 150px;

                        :global(button) {
                            width: 100%;
                        }
                    }
                }
            }
        }

    }

    @container wrap (max-width: 400px) {
        div.s8-subheading {
            flex-direction: column;
            row-gap: 5px;

            ul {
                width: 100%;

                &.s8-brands {
                    justify-content: space-between;
                }
            }
        }
    }

    @container wrap (max-width: 760px) {
        div.s8-wrap-bottom {
            flex-direction: column;
            row-gap: 20px;
        }
    }

    @container info (max-width: 620px) {
        li {
            width: calc(50% - 10px/2) !important;
        }
    }

    @container info (max-width: 420px) {
        li {
            width: 100% !important;
        }
    }

    @container buttons (max-width: 480px) {
        li {
            width: 100% !important;
        }
    }

    @container buttons (max-width: 300px) {
        ul {
            flex-direction: column !important;
            row-gap: 10px;
        }
    }

</style>

<!-- eslint-enable -->